import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import { EASE_OUT_EXPO } from '../../../../components/AnimatedSection/AnimatedSection';
import './ContactFormSection.scss';
import contactImage from '../../../../assets/images/contact/contact-hero.webp';

const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const ContactFormSection = () => {
    const [formData, setFormData] = useState({ firstName: '', email: '', message: '' });
    const [messageLength, setMessageLength] = useState(0);
    const MAX_LENGTH = 180;
    const [errors, setErrors] = useState({});
    const [submitStatus, setSubmitStatus] = useState(null);
    const { t } = useTranslation();
    const prefersReducedMotion = useReducedMotion();

    const fadeUp = (delay, duration = 1.0) =>
        prefersReducedMotion
            ? {}
            : {
                  initial: { opacity: 0, y: 20 },
                  whileInView: { opacity: 1, y: 0 },
                  viewport: { once: true, amount: 0.25 },
                  transition: { duration, delay, ease: EASE_OUT_EXPO },
              };

    const slideLeft = prefersReducedMotion
        ? {}
        : {
              initial: { opacity: 0, x: -40 },
              whileInView: { opacity: 1, x: 0 },
              viewport: { once: true, amount: 0.25 },
              transition: { duration: 1.2, ease: EASE_OUT_EXPO },
          };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (name === 'message') setMessageLength(value.length);
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
        if (submitStatus) setSubmitStatus(null);
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.firstName.trim()) newErrors.firstName = t('validation_name_required');
        if (!formData.email.trim()) newErrors.email = t('validation_email_required');
        else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = t('validation_email_invalid');
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const newErrors = validateForm();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setSubmitStatus('sending');

        try {
            await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, {
                from_name: formData.firstName,
                from_email: formData.email,
                message: formData.message,
            }, EMAILJS_PUBLIC_KEY);

            setSubmitStatus('success');
            setFormData({ firstName: '', email: '', message: '' });
            setMessageLength(0);
            setErrors({});
        } catch {
            setSubmitStatus('error');
        }
    };

    return (
        <section className="contact-form-section">
            <div className="contact-form-section__container">
                <div className="contact-form-section__row">
                    <motion.div className="contact-form-section__image-col" {...slideLeft}>
                        <div className="contact-form-section__image-wrapper">
                            <img
                                src={contactImage}
                                alt={t('alt_church')}
                                className="contact-form-section__image"
                            />
                        </div>
                        <a
                            href="https://usaregeneration.churchcenter.com/people/forms/394490"
                            className="contact-form-section__join-button"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {t('join_church')}
                        </a>
                    </motion.div>

                    <motion.div className="contact-form-section__form-col" {...fadeUp(0.15)}>
                        <div className="contact-form-section__header">
                            <h2 className="contact-form-section__title">{t('ask_question')}</h2>
                            <div className="contact-form-section__divider"></div>
                        </div>
                        <p className="contact-form-section__description">
                            {t('form_description')}
                        </p>

                        <form onSubmit={handleSubmit} noValidate>
                            <div className="contact-form-section__field">
                                <label htmlFor="firstName" className="contact-form-section__label">
                                    {t('first_name')}
                                </label>
                                <input
                                    type="text"
                                    id="firstName"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    className={`contact-form-section__input${errors.firstName ? ' contact-form-section__input--error' : ''}`}
                                    aria-required="true"
                                    aria-invalid={!!errors.firstName}
                                    aria-describedby={errors.firstName ? 'firstName-error' : undefined}
                                    placeholder={t('placeholder_name')}
                                />
                                {errors.firstName && (
                                    <div id="firstName-error" className="contact-form-section__error" role="alert">
                                        {errors.firstName}
                                    </div>
                                )}
                            </div>

                            <div className="contact-form-section__field">
                                <label htmlFor="email" className="contact-form-section__label">
                                    {t('email_address')}
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className={`contact-form-section__input${errors.email ? ' contact-form-section__input--error' : ''}`}
                                    aria-required="true"
                                    aria-invalid={!!errors.email}
                                    aria-describedby={errors.email ? 'email-error' : undefined}
                                    placeholder={t('placeholder_email')}
                                />
                                {errors.email && (
                                    <div id="email-error" className="contact-form-section__error" role="alert">
                                        {errors.email}
                                    </div>
                                )}
                            </div>

                            <div className="contact-form-section__field">
                                <label htmlFor="message" className="contact-form-section__label">
                                    {t('message')}
                                </label>
                                <textarea
                                    id="message"
                                    name="message"
                                    value={formData.message}
                                    onChange={handleChange}
                                    maxLength={MAX_LENGTH}
                                    rows="5"
                                    className="contact-form-section__textarea"
                                    placeholder={t('placeholder_message')}
                                />
                                <div className="contact-form-section__counter">{messageLength} / {MAX_LENGTH}</div>
                            </div>

                            <button
                                type="submit"
                                className="contact-form-section__submit"
                                disabled={submitStatus === 'sending'}
                            >
                                {submitStatus === 'sending' ? t('sending') || 'Sending...' : t('submit')}
                            </button>

                            {submitStatus === 'success' && (
                                <div className="contact-form-section__success" role="status" aria-live="polite">
                                    {t('form_success')}
                                </div>
                            )}
                            {submitStatus === 'error' && (
                                <div className="contact-form-section__error contact-form-section__error--form" role="alert">
                                    {t('form_error') || 'Something went wrong. Please try again.'}
                                </div>
                            )}
                        </form>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default ContactFormSection;
